import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from '../../ui/Button';
import GroupReturnRequestButton from './GroupReturnRequestButton';
import {
    TruckIcon,
    ArchiveBoxIcon,
    ChatBubbleLeftEllipsisIcon,
    TicketIcon,
    MapPinIcon,
    ArrowPathIcon,
    BuildingStorefrontIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'; 
import { CheckCircleIcon as SolidCheckCircleIcon } from '@heroicons/react/24/solid';

// Define userLang here if it's simple, or pass as prop if it's more dynamic
const userLang = navigator.language || navigator.userLanguage;

const FulfillmentGroupActionButtons = ({
    status,
    groupType,
    isMerchantContext,
    isUserContext,
    merchant,
    outboundDeliveries = [],
    returnDeliveries = [],
    fgId,
    orderId,
    parentOrder,
    fulfillmentGroup,
    // Callbacks from parent
    handleContactUser,
    handleGroupAction,
    showConfirmationModal,
    // State from parent
    isCancelingGroup,
}) => {
    const history = useHistory();
    const user = useSelector(state => state.auth.user);
    const loggedInUserId = user?.id;

    let actionDetails = null;
    let secondaryActionDetails = null;
    let tertiaryActionDetails = null;

    const commonButtonClass = "text-xs whitespace-nowrap justify-center sm:w-auto px-3 py-1.5";
    const currentStatusLower = status?.toLowerCase();

    const customerCancellableGroupStatuses = ['processing', 'coordinating_pickup'];
    const merchantCancellableGroupStatuses = ['processing', 'coordinating_pickup'];
    const orderCreatedAt = parentOrder ? new Date(parentOrder.created_at) : new Date();
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    if (currentStatusLower === 'canceled') {
        actionDetails = null;
        secondaryActionDetails = null;
        
        // Always show contact shop for canceled orders in customer view
        if (isUserContext && merchant) {
            let contactTarget = merchant;
            tertiaryActionDetails = {
                key: 'contact-shop-after-cancel',
                onClick: () => handleContactUser(contactTarget),
                variant: 'secondary',
                Icon: ChatBubbleLeftEllipsisIcon,
                label: userLang === 'zh-CN' ? '联系店铺' : 'Contact Shop',
            };
        }
    } else {
        const outboundShipmentDelivery = outboundDeliveries?.find(d => d.deliveryLegType === 'outbound_group');

        if (isMerchantContext) {
            if (groupType === 'shipping') {
                if (outboundShipmentDelivery?.tracking_url &&
                    (currentStatusLower === 'shipped_to_customer' || currentStatusLower === 'ready_to_ship')) {
                    actionDetails = {
                        key: 'track-shipment-merchant',
                        onClick: () => window.open(outboundShipmentDelivery.tracking_url, '_blank'),
                        variant: 'primary', Icon: TruckIcon, label: userLang === 'zh-CN' ? '跟踪货运' : 'Track Shipment',
                    };
                } else if (currentStatusLower === 'processing' || currentStatusLower === 'ready_to_ship') {
                    actionDetails = {
                        key: 'start-shipping',
                        onClick: () => handleGroupAction('startShipping'),
                        variant: 'primary', Icon: TruckIcon, label: userLang === 'zh-CN' ? '开始发货' : 'Start Shipping',
                    };
                }
                
                // Handle return shipment tracking and actions for merchants
                if (!actionDetails && (currentStatusLower === 'awaiting_return_shipment' || currentStatusLower === 'return_in_transit_to_merchant')) {
                    const firstReturnDelivery = returnDeliveries.find(d => d.tracking_url || d.shipping_label_url);
                    if (firstReturnDelivery && (currentStatusLower === 'awaiting_return_shipment' || currentStatusLower === 'return_in_transit_to_merchant')) {
                        // Show track return shipment for merchants just like customers
                        actionDetails = {
                            key: 'track-return-shipment-merchant',
                            onClick: () => window.open(firstReturnDelivery.tracking_url || firstReturnDelivery.shipping_label_url, '_blank'),
                            variant: 'primary', Icon: ArrowPathIcon, label: userLang === 'zh-CN' ? '跟踪退货' : 'Track Return Shipment',
                        };
                    }
                    
                    // Add return received button for return_in_transit_to_merchant status
                    if (currentStatusLower === 'return_in_transit_to_merchant') {
                        secondaryActionDetails = {
                            key: 'mark-return-received-merchant',
                            onClick: () => handleGroupAction('markReturnReceived'),
                            variant: 'secondary', Icon: ArchiveBoxIcon, label: userLang === 'zh-CN' ? '标记退货已收到' : 'Return Received',
                        };
                    }
                } else if (!actionDetails && currentStatusLower === 'return_delivered_to_merchant') {
                    actionDetails = {
                        key: 'process-group-return',
                        onClick: () => handleGroupAction('processGroupReturn'),
                        variant: 'primary', Icon: ArchiveBoxIcon, label: userLang === 'zh-CN' ? '处理退货组' : 'Process Group Return',
                    };
                } else if (!actionDetails && currentStatusLower === 'return_processing_by_merchant') {
                    // Add confirm return completed button for merchants processing returns
                    actionDetails = {
                        key: 'confirm-return-completed-merchant',
                        onClick: () => showConfirmationModal({
                            type: 'confirmReturnCompletedConfirmation',
                            title: userLang === 'zh-CN' ? '确认退货完成' : 'Confirm Return Completed',
                            message: userLang === 'zh-CN' ? '确认已完成此退货组的处理？这将完成退货流程。' : 'Confirm that you have completed processing this return group? This will finalize the return process.',
                            confirmLabel: userLang === 'zh-CN' ? '确认完成' : 'Confirm Completed', confirmVariant: 'primary'
                        }),
                        variant: 'primary', Icon: SolidCheckCircleIcon, label: userLang === 'zh-CN' ? '确认退货完成' : 'Confirm Return Completed',
                    };
                }
            } else if (groupType === 'pickup') {
                if (currentStatusLower === 'ready_for_pickup') {
                    actionDetails = {
                        key: 'coordinate-pickup-chat-merchant',
                        onClick: () => history.push(`/message/fulfillment-group/${fgId}`),
                        variant: 'primary', label: userLang === 'zh-CN' ? '协调取货' : 'Coordinate Pickup',
                    };
                }
            }

        } else if (isUserContext) { // Buyer context
            if (groupType === 'shipping') {
                if (currentStatusLower === 'delivery_completed_to_customer') {
                    // For rental orders, keep existing behavior
                    if (parentOrder && parentOrder.orderType === 'rental') {
                        actionDetails = {
                            key: 'initiate-group-return',
                            onClick: () => handleGroupAction('initiateGroupReturn'),
                            variant: 'primary', Icon: ArrowPathIcon, label: userLang === 'zh-CN' ? '发起整组退货' : 'Initiate Group Return',
                        };
                    }
                    // For buy orders, check if group return is possible
                    else {
                        // Check if there are eligible buy items for group return
                        const eligibleBuyItems = fulfillmentGroup?.orderItems?.filter(item => {
                            const itemStatus = item.status?.toLowerCase();
                            return item.type === 'buy' && 
                                   !item.returnRequest && // no existing request (even canceled)
                                   !itemStatus?.includes('return') && 
                                   itemStatus !== 'canceled' &&
                                   itemStatus !== 'pending';
                        }) || [];

                        // If there are eligible buy items, show group return button
                        if (eligibleBuyItems.length > 0) {
                            // Use a special key for group return component
                            actionDetails = {
                                key: 'group-return-buy-items',
                                component: 'GroupReturnRequestButton',
                                fulfillmentGroupId: fgId
                            };
                        }
                    }
                } else if (currentStatusLower === 'shipped_to_customer' && outboundShipmentDelivery?.tracking_url) {
                    // When shipped, show only Track Shipment button (not both Track and Confirm)
                    actionDetails = {
                        key: 'track-shipment-user',
                        onClick: () => window.open(outboundShipmentDelivery.tracking_url, '_blank'),
                        variant: 'primary', Icon: TruckIcon, label: userLang === 'zh-CN' ? '跟踪货运' : 'Track Shipment',
                    };
                } else if (outboundShipmentDelivery?.tracking_url && currentStatusLower === 'ready_to_ship') {
                    // Keep just Track Shipment for ready_to_ship status
                    actionDetails = {
                        key: 'track-shipment-user',
                        onClick: () => window.open(outboundShipmentDelivery.tracking_url, '_blank'),
                        variant: 'primary', Icon: TruckIcon, label: userLang === 'zh-CN' ? '跟踪货运' : 'Track Shipment',
                    };
                } else if (!actionDetails && (currentStatusLower === 'awaiting_return_shipment' || currentStatusLower === 'awaiting_return_dropoff')) {
                    const firstReturnDelivery = returnDeliveries.find(d => d.tracking_url || d.shipping_label_url);
                    if (firstReturnDelivery) {
                        actionDetails = {
                            key: 'track-group-return',
                            onClick: () => window.open(firstReturnDelivery.tracking_url || firstReturnDelivery.shipping_label_url, '_blank'),
                            variant: 'secondary', Icon: ArrowPathIcon, label: userLang === 'zh-CN' ? '跟踪退货' : 'Track Return Shipment',
                        };
                    }
                }
            } else if (groupType === 'pickup') {
                if (currentStatusLower === 'ready_for_pickup') {
                    actionDetails = {
                        key: 'coordinate-pickup-chat-user',
                        onClick: () => history.push(`/message/fulfillment-group/${fgId}`),
                        variant: 'primary', label: userLang === 'zh-CN' ? '协调取货' : 'Coordinate Pickup',
                    };
                } else if (currentStatusLower === 'pickup_completed_by_customer') {
                    // Contact shop button will be added later in the tertiaryActionDetails section
                    // Review and return options now handled at the item level in OrderItemCard
                }
            }
        }

        const nonCancellableGroupStatuses = ['shipped_to_customer', 'pickup_completed_by_customer', 'delivery_completed_to_customer', 'return_in_transit_to_merchant', 'return_delivered_to_merchant', 'return_completed_for_group', 'canceled'];
        const hasActiveShipment = outboundDeliveries?.some(delivery =>
            delivery.deliveryLegType === 'outbound_group' &&
            ['label_created', 'Shipped', 'Delivered'].includes(delivery.status)
        );

        if (!nonCancellableGroupStatuses.includes(currentStatusLower) && !hasActiveShipment) {
            if (isMerchantContext && merchantCancellableGroupStatuses.includes(currentStatusLower)) {
                if (!secondaryActionDetails) {  // Only set if not already set by track shipment
                    secondaryActionDetails = {
                        key: 'cancel-group-merchant',
                        onClick: () => showConfirmationModal({
                            type: 'cancelGroupConfirmation',
                            title: userLang === 'zh-CN' ? '确认取消处理组' : 'Confirm Group Cancellation',
                            message: userLang === 'zh-CN' ? '这将取消此处理组中的所有商品并向客户退款。确定要继续吗？' : 'This will cancel all items in this fulfillment group and initiate a refund to the customer. Are you sure you want to proceed?',
                            confirmLabel: userLang === 'zh-CN' ? '确认取消' : 'Confirm Cancel', confirmVariant: 'danger'
                        }),
                        variant: 'secondary', Icon: XMarkIcon, label: userLang === 'zh-CN' ? '取消' : 'Cancel', className: 'text-red-600 border-red-600 hover:bg-red-50'
                    };
                }
            } else if (isUserContext && customerCancellableGroupStatuses.includes(currentStatusLower) && orderCreatedAt >= twentyFourHoursAgo) {
                if (!secondaryActionDetails) {  // Only set if not already set by track shipment
                    secondaryActionDetails = {
                        key: 'cancel-group-user',
                        onClick: () => showConfirmationModal({
                            type: 'cancelGroupConfirmation',
                            title: userLang === 'zh-CN' ? '确认取消处理组' : 'Confirm Group Cancellation',
                            message: userLang === 'zh-CN' ? '这将取消此处理组中的所有商品并启动退款流程。确定要继续吗？' : 'This will cancel all items in this fulfillment group and initiate the refund process. Are you sure you want to proceed?',
                            confirmLabel: userLang === 'zh-CN' ? '确认取消' : 'Confirm Cancel', confirmVariant: 'danger'
                        }),
                        variant: 'secondary', Icon: XMarkIcon, label: userLang === 'zh-CN' ? '取消' : 'Cancel', className: 'text-red-600 border-red-600 hover:bg-red-50'
                    };
                }
            }
        }

        // Add Contact Shop button when appropriate
        if (actionDetails?.key !== 'coordinate-pickup-chat-user' &&
            actionDetails?.key !== 'coordinate-pickup-chat-merchant' &&
            groupType !== 'pickup') {
            if (isUserContext && merchant) {
                let contactTarget = merchant;
                tertiaryActionDetails = {
                    key: 'contact-shop',
                    onClick: () => handleContactUser(contactTarget),
                    variant: 'secondary', Icon: ChatBubbleLeftEllipsisIcon, label: userLang === 'zh-CN' ? '联系店铺' : 'Contact Shop',
                };
            }
        } else if (groupType === 'pickup' && isUserContext && merchant && 
                  (currentStatusLower === 'pickup_completed_by_customer' || currentStatusLower === 'canceled')) {
            // Also add contact shop for pickup completed and canceled statuses
            let contactTarget = merchant;
            tertiaryActionDetails = {
                key: 'contact-shop-pickup',
                onClick: () => handleContactUser(contactTarget),
                variant: 'secondary', Icon: ChatBubbleLeftEllipsisIcon, label: userLang === 'zh-CN' ? '联系店铺' : 'Contact Shop',
            };
        }
    }

    const orderedButtons = [];
    if (tertiaryActionDetails) orderedButtons.push(tertiaryActionDetails);
    if (secondaryActionDetails) orderedButtons.push(secondaryActionDetails);
    if (actionDetails) orderedButtons.push(actionDetails);

    if (orderedButtons.length === 0) {
        return null; 
    }

    return (
        <div className="flex items-center gap-x-1 sm:gap-x-2 flex-wrap justify-center sm:justify-end">
            {orderedButtons.map(btnAction => {
                // Special handling for GroupReturnRequestButton component
                if (btnAction.component === 'GroupReturnRequestButton') {
                    return (
                        <GroupReturnRequestButton
                            key={btnAction.key}
                            fulfillmentGroupId={btnAction.fulfillmentGroupId}
                            onSuccess={() => {
                                // Optionally trigger a refetch or state update
                                console.log('Group return request submitted successfully');
                            }}
                        />
                    );
                }
                
                // Regular button rendering
                return (
                    <Button
                        key={btnAction.key}
                        onClick={btnAction.onClick}
                        variant={btnAction.variant}
                        className={`${commonButtonClass} ${btnAction.className || ''}`}
                        disabled={btnAction.disabled || (isCancelingGroup && btnAction.key.includes('cancel'))}
                        isLoading={btnAction.isLoading || (isCancelingGroup && btnAction.key.includes('cancel'))}
                    >
                        {btnAction.Icon && <btnAction.Icon className="hidden sm:inline h-4 w-4 mr-1.5" />}
                        {btnAction.label}
                    </Button>
                );
            })}
        </div>
    );
};

export default FulfillmentGroupActionButtons; 